import React from 'react';
import { Card, Drawer } from 'antd';
import { CloseIcon, ExtensionIcon, ModalCallIcon, ModalHeaderBar, UserIcon } from 'assets';
import { AvatarToolTip, Loader, Typography } from 'components/common';
import { CallCenterFlow, CallFlow, CallHistoryRecord, IVRFlow } from 'redux/slices/callHistory.slice';
import './infoDrawer.scss';
import { formatPhoneNumber, formatSeconds } from 'utils';
import { getCallStatus } from 'utils/getCallStatus';

interface DetailItem {
  icon?: string;
  label: string;
  value?: string | number | null;
}

interface CardDetailItem {
  card: true;
  key: string;
  content: React.ReactNode;
}

export interface HHAData {
  RecordType: 'Patient' | 'Caregiver';
  ID?: string;
  Name?: string;
  Phone?: string;
  AssignedTo?: string;
  Company?: string;
}

interface Section {
  sectionTitle: string;
  items: (DetailItem | CardDetailItem)[];
}

interface InfoDrawerProps {
  title?: string;
  open: boolean;
  onCancel: () => void;
  hhaexchangeData?: HHAData;
  callerDetails?: DetailItem[];
  additionalDetails?: DetailItem[];
  recentHistory?: CallHistoryRecord[];
  historyLoading?: boolean;
  agents?: { extension: string | number; name: string }[];
  ivrMenus?: { id:string; extension: string; name: string }[];
  queues?: { id:string; extension: string; name: string }[];
  customDetailsSection?: {
    title: string;
    items: DetailItem[];
  };
}

const InfoDrawer: React.FC<InfoDrawerProps> = ({
  title = 'Info',
  open,
  onCancel,
  hhaexchangeData,
  callerDetails,
  additionalDetails,
  recentHistory,
  historyLoading,
  agents = [],
  ivrMenus=[],
  queues=[],
  customDetailsSection
}) => {
  const hasHhaData = !!hhaexchangeData?.ID && !!hhaexchangeData?.Name;

  const hhaSection: Section | null = hasHhaData
    ? {
      sectionTitle: 'HHAeXchange',
      items: [
        { icon: UserIcon, label: 'Record Type', value: hhaexchangeData?.RecordType },
        {
          icon: UserIcon,
          label: `${hhaexchangeData?.RecordType === 'Patient' ? 'Patient' : 'Caregiver'} Name`,
          value: hhaexchangeData?.Name,
        },
        { icon: UserIcon, label: 'Contact Number', value: hhaexchangeData?.Phone },
        ...(hhaexchangeData?.RecordType === 'Patient'
          ? [{ icon: UserIcon, label: 'Assigned To', value: hhaexchangeData?.AssignedTo }]
          : [{ icon: UserIcon, label: 'Company', value: hhaexchangeData?.Company }]),
      ],
    }
    : null;

  const getFilledItems = (items: DetailItem[] = [], defaultIcon?: string): DetailItem[] =>
  items.map((item) => ({
    ...item,
    icon: item.icon ?? defaultIcon,
    value: item.value ?? '-',
  }));


  const historySection: Section = {
    sectionTitle: 'Recent Call History',
    items: historyLoading
      ? [
        {
          card: true,
          key: 'call-history-loading',
          content: (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
              <Loader size="large" />
            </div>
          ),
        },
      ]
      : recentHistory && recentHistory.length > 0
        ? recentHistory.slice(0, 5).map((record, idx) => {
          const flows: CallFlow[] = record.call_flow?.flows || [];

          const callCenterFlow = [...flows].reverse().find(
            (flow): flow is CallCenterFlow => flow.application?.app === 'callcenter'
          );

          const ivrFlow = flows.find(
            (flow): flow is IVRFlow => flow.application?.app === 'ivr'
          );

          const answeredFlow = flows.find(flow => flow.answered && flow.destination);

          const callStatus = getCallStatus(record);


          let nameLabel = null;
          let nameValue = null;

          if (record.call_direction === 'inbound') {
            if (callCenterFlow?.callcenter?.agentNumber) {
              const matchedAgent = agents.find(agent => `${agent.extension}` === `${callCenterFlow.callcenter.agentNumber}`);
              if (matchedAgent?.name) {
                nameLabel = "Agent";
                nameValue = matchedAgent.name + " (" +matchedAgent.extension+")";
              }
            } else if (!callCenterFlow && ivrFlow?.answered) {
  const ivrId = ivrFlow.application?.data;
  const matchedIVR = ivrMenus?.find(menu => menu.id === ivrId);

  if (matchedIVR) {
    nameLabel = "Application";
    nameValue = matchedIVR.extension
      ? `${matchedIVR.name} (${matchedIVR.extension})`
      : matchedIVR.name;
  } else {
    nameLabel = "Application";
    nameValue = "IVR";
  }
}

          } else if (record.call_direction === 'outbound') {
  if (record.user_ext) {
    const matchedAgent = agents.find(agent => `${agent.extension}` === `${record.user_ext}`);
    if (matchedAgent) {
      nameLabel = "Agent";
      nameValue = `${matchedAgent.name} (${matchedAgent.extension})`;
    } else {
      nameLabel = "Caller";
      nameValue = formatPhoneNumber(record.caller_id_number);
    }
  } else {
    nameLabel = "Caller";
    nameValue = formatPhoneNumber(record.caller_id_number);
  }
  
}

let queueLabel = null;
let queueValue = null;

if (callCenterFlow?.callcenter?.queueID && queues?.length) {
  const matchedQueue = queues.find(q => q.id === callCenterFlow.callcenter.queueID);
  if (matchedQueue) {
    queueLabel = "Queue";
    queueValue = `${matchedQueue.name} (${matchedQueue.extension ?? '-'})`;
  }
}





          const directionLabel =
            record.call_direction === 'inbound' ? 'Incoming' : 'Outgoing';
          const date = new Date(record.answer_timestamp || record.start_timestamp);
          const formattedDate = date.toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
          });

          const duration =
  callCenterFlow?.callcenter?.status === "answered"
    ? callCenterFlow.flow_duration
    : ivrFlow?.answered
    ? ivrFlow.flow_duration
    : answeredFlow?.flow_duration ?? null;

          return {
            card: true,
            key: `call-history-${idx}`,
            content: (
              <Card
                key={`call-history-${idx}`}
                size="small"
                style={{ marginBottom: '1rem', borderRadius: '8px' }}
                bordered
                title={`${formattedDate} — ${directionLabel}`}
              >
                <div className="call-history-item">
                  <div>
                    <Typography variant="small-heading-3">
                      <strong>Dialed Number:</strong>{' '}
                      {record.dialed_number ? formatPhoneNumber(record.dialed_number) : '-'}
                    </Typography>

                  </div>
                  {record?.call_direction === "inbound" &&
                  <div>
                    <div className="dialed-extensions">
                      <Typography variant="small-heading-3">
                        <strong>Dialed Extensions:</strong>
                      </Typography>
                      {record.dialed_extensions?.length ? (
                        <AvatarToolTip
                          items={record.dialed_extensions.map((ext) => ({
                            label: `Extension: ${ext}`,
                            value: ext,
                          }))}
                        />
                      ) : (
                        <Typography variant="small-heading-3">No dialed extensions</Typography>
                      )}
                    </div>
                  </div>
                  }
                  <div>
                    <Typography variant="small-heading-3">
                      <strong>Call Status:</strong>{" "}
                      {callStatus.charAt(0).toUpperCase() + callStatus.slice(1)}
                    </Typography>
                  </div>

                  {nameLabel && nameValue && (
                    <div>
                      <Typography variant="small-heading-3">
                        <strong>{nameLabel}:</strong> {nameValue}
                      </Typography>
                    </div>
                  )}

                  {queueLabel && queueValue && (
                    <div>
                      <Typography variant="small-heading-3">
                        <strong>{queueLabel}:</strong> {queueValue}
                      </Typography>
                    </div>
                  )}

                  {typeof duration === 'number' && duration > 0 && (
                    <div>
                      <Typography variant="small-heading-3">
                        <strong>Call Duration:</strong> {formatSeconds(duration)}
                      </Typography>
                    </div>
                  )}



                </div>
              </Card>
            ),
          };
        })
        : [
          {
            card: true,
            key: 'call-history-empty',
            content: (
              <div style={{ padding: '1rem', textAlign: 'center' }}>
                <Typography variant="body-text-3">No recent calls found.</Typography>
              </div>
            ),
          },
        ],
  };

  const callSections: Section[] = [
    /*{
      sectionTitle: 'Caller Details',
      items: getFilledItems(callerDetails || [], ExtensionIcon),
    },
    */
    ...(customDetailsSection
  ? [{
      sectionTitle: customDetailsSection.title,
      items: getFilledItems(customDetailsSection.items, UserIcon),
    }]
  : additionalDetails
  ? [{
      sectionTitle: 'Additional Details',
      items: getFilledItems(additionalDetails, UserIcon),
    }]
  : []),

    ...(hhaSection ? [hhaSection] : []),
    historySection,
  ];

  return (
    <Drawer
      open={open}
      onClose={onCancel}
      placement="left"
      closable={false}
      width={400}
      className="info-drawer-container"
    >
      <div className="drawer-close-icon" onClick={onCancel}>
        <img src={CloseIcon} alt="close-icon" />
      </div>

      <div className="drawer-info-container">
        <img src={ModalHeaderBar} alt="drawer-header-bar" className="drawer-header-bar-img" />
        <div className="drawer-call-icon-img">
          <img src={ModalCallIcon} alt="drawer-call-icon" />
        </div>
        <Typography variant="subText" className="main-heading">
          Call Details
        </Typography>

        <div className="drawer-scroll-wrapper">
          {callSections.map((section, idx) => (
            <div className="caller-info-container" key={idx}>
              <Typography variant="subText" className="section-heading">
                {section.sectionTitle}
              </Typography>

              {section.items.map((item, itemIdx) =>
                'card' in item ? (
                  <div key={item.key}>{item.content}</div>
                ) : (
                  <div className="caller-info" key={`${item.label}-${itemIdx}`}>
                    <img src={item.icon} alt="icon" />
                    <Typography variant="subText-2" className="info-label">
                      {item.label}
                    </Typography>
                    <Typography variant="body-text-3" className="info-value">
                      {item.value ?? '-'}
                    </Typography>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
};

export default InfoDrawer;
